/**
 * Shared user-facing error and completion checks.
 * A short sentence is not safe just because it is short.
 * Completion still requires the artifact the module promised.
 */

const LEAK = /prisma|\bsql\b|\bselect\s+.+\s+from\b|\binsert\s+into\b|\bupdate\s+\w+\s+set\b|stack trace|\btypeerror\b|\breferenceerror\b|\bsyntaxerror\b|\beconn|\bjwt\b|sequelize|postgres|node_modules|cannot read propert|cannot set propert|module\s*not\s*found|no module named|importerror|traceback|lifelines|statsmodels|numpy[_-]?financial|\bumap\b|prophet|sklearn|scikit|pip install|\blibrary\b|\bat\s+\//i;

function classifyPrerequisite(message) {
  const raw = String(message || '');
  const hit = (kind, tier, content) => ({ kind, tier, content });
  if (/eval run required before promotion|latest eval score/i.test(raw)) {
    return hit('agent_eval', 'authorization', 'This agent needs a successful evaluation before it can be promoted. I can help you run the evaluation first.');
  }
  if (/sign in with an email before turning alerts/i.test(raw)) {
    return hit('account_email', 'authorization', 'Alerts need an email address on your account. Add one in account settings, and I can turn them on after that. I will not enable alerts without it.');
  }
  if (/package is locked|report is certified|certified version/i.test(raw)) {
    return hit('report_revision', 'authorization', 'This report is certified, so I can’t change that copy. I can create a revision and continue there.');
  }
  if (/create a cycle first/i.test(raw)) {
    return hit('okr_cycle', 'clarification', 'You don’t have an active OKR cycle yet. I can open one and then continue with what you asked.');
  }
  if (/draft objectives first|no key results/i.test(raw)) {
    return hit('okr_objectives', 'clarification', 'This cycle has no key results yet. What should the main objective be?');
  }
  if (/create a monitor before|create a monitor first|monitor not found|no data available to monitor/i.test(raw)) {
    return hit('sentinel_monitor', 'clarification', 'Sure. First, what KPI or measure should I monitor?');
  }
  if (/choose a workflow first|choose a workflow before|create a workflow before|schedule a workflow before|there is no workflow/i.test(raw)) {
    return hit('workflow', 'clarification', 'What should I watch? I’ll set that up, then continue with the alerts.');
  }
  if (/look up that competitor first/i.test(raw)) {
    return hit('competitor', 'clarification', 'Which competitor should I look up?');
  }
  if (/company name and an industry|complete your (company|business) profile/i.test(raw)) {
    return hit('profile', 'clarification', 'I need a company name and an industry on your profile before I can do that.');
  }
  if (/dataset not found or empty|datasetid is required|selected dataset is not ready|select a dataset first|choose a data file first|module not ready/i.test(raw)) {
    return hit('dataset', 'clarification', 'Which data source should I use? I’ll continue with what you asked.');
  }
  if (/owner or admin needs to/i.test(raw)) {
    return hit('permission', 'permission', 'An owner or admin needs to do that. I can keep your request ready.');
  }
  if (/upgrade your plan|not included in your plan|plan does not include|payment required/i.test(raw)) {
    return hit('plan_limit', 'paywall', 'This action needs a plan that includes it. I can show you the upgrade options.');
  }
  if (/authorize the integration|reconnect the integration|connect quickbooks|connect xero|connect slack before/i.test(raw)) {
    return hit('integration_auth', 'authorization', 'That needs an authorized connection. Connect it in Data Hub, and I’ll continue with what you asked.');
  }
  return null;
}

function sanitizeUserFacingError(value, fallback = 'Something went wrong. Please try again.') {
  const msg = typeof value === 'string'
    ? value.trim()
    : (value && typeof value.message === 'string' ? value.message.trim() : '');
  if (!msg) return fallback;
  const guided = classifyPrerequisite(msg);
  if (guided) return guided.content;
  if (LEAK.test(msg) || msg.length > 220) return fallback;
  return msg;
}

function isTechnicalDependencyError(value) {
  return LEAK.test(String(value || ''));
}

function statementReady(statement) {
  return Boolean(statement && statement.id);
}

function deckReady(deck) {
  return Boolean(
    deck
    && (deck.presentation_id || deck.id)
    && Array.isArray(deck.slides)
    && deck.slides.length > 0,
  );
}

function recordId(value) {
  if (!value || typeof value !== 'object') return '';
  return String(value.id || value.presentation_id || value.source_id || value.pipeline?.id || value.run?.id || value.report?.id || '').trim();
}

function visionSessionReady(session) {
  if (!session?.id) return false;
  const meaningful = (result) => {
    if (typeof result === 'string') return Boolean(result.trim());
    return Boolean(result && typeof result === 'object' && Object.keys(result).length);
  };
  if (meaningful(session.result)) return true;
  return Array.isArray(session.turns) && session.turns.some((turn) => meaningful(turn?.result));
}

function artifactReady(kind, value) {
  switch (kind) {
    case 'vision-session':
      return visionSessionReady(value);
    case 'vision-pipeline':
      return Boolean(value?.id || value?.pipeline?.id);
    case 'vision-run':
      return Boolean(value?.run?.id || value?.id);
    case 'okr-cycle':
    case 'okr-objective':
    case 'agent':
    case 'dashboard':
      return Boolean(recordId(value));
    case 'okr-review':
      return Boolean(value?.id);
    case 'widgets':
      return Array.isArray(value) && value.length > 0 && value.every((widget) => recordId(widget));
    case 'widget':
      return Boolean(recordId(value));
    case 'dossier':
      return Boolean(value && (value.narrative || value.summary || value.metrics));
    case 'help-answer':
      return Boolean(String(value || '').trim());
    case 'presentation-source':
    case 'report':
      return Boolean(recordId(value) || recordId(value?.data) || recordId(value?.report));
    default:
      return false;
  }
}

export {
  classifyPrerequisite,
  sanitizeUserFacingError,
  isTechnicalDependencyError,
  statementReady,
  deckReady,
  artifactReady,
};
